/**
 * EPG Event Store
 *
 * Manages event options, field definitions, and event data by month.
 * Split from monolithic epgStore.ts for better maintainability.
 *
 * Created: 2025-12-22
 * Phase: Store Split Refactoring
 */

import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import { logger } from '@shared/utils/logger';
import { epgApi } from '@shared/services/epgApi';
import { getCategoryOptions, normalizeCategoryName } from '@features/epg/config/epgConfig';
import type {
  EventsByMonth,
  EventOption,
  EventOptionsResponse,
  CategoryEventOptions,
  EventOptionsByCategory,
  ContactInfo,
  FieldDefinition,
  FieldDefinitionsResponse,
  MonthCode,
} from '@shared/types/epg';

/**
 * Event Store State
 */
interface EventState {
  // ======================================
  // Events by Month
  // ======================================
  eventsByMonth: Record<string, EventsByMonth> | null; // MonthCode -> EventsByMonth

  // ======================================
  // Event Options (Palette)
  // ======================================
  eventOptions: EventOptionsResponse | null; // Legacy - keep for backward compatibility
  eventOptionsByCategory: EventOptionsByCategory;
  isLoadingEventOptions: boolean;

  // ======================================
  // Organization Contacts
  // ======================================
  organizationContacts: ContactInfo[];

  // ======================================
  // Field Definitions (Pipedrive Parity)
  // ======================================
  fieldDefinitions: Record<string, FieldDefinition> | null; // field_key -> definition
  fieldDefinitionsCategories: Record<string, string[]> | null; // category -> field_keys[]
  fieldDefinitionsLastSync: string | null; // ISO timestamp
  isLoadingFieldDefinitions: boolean;
  isSyncingFieldDefinitions: boolean;

  // ======================================
  // Loading & Error
  // ======================================
  isLoadingEvents: boolean;
  error: string | null;

  // ======================================
  // Actions: Event Options
  // ======================================
  getStaticEventOptions: (category: 'Events' | 'Calls & Emails' | 'Drops') => CategoryEventOptions;
  transformEventOptions: (
    response: EventOptionsResponse | null,
    category: 'Events' | 'Calls & Emails' | 'Drops'
  ) => CategoryEventOptions;
  loadEventOptions: (category: 'Events' | 'Calls & Emails' | 'Drops') => Promise<void>;

  // ======================================
  // Actions: Field Definitions
  // ======================================
  loadFieldDefinitions: (category?: string) => Promise<void>;
  syncFieldDefinitions: () => Promise<void>;

  // ======================================
  // Actions: Events by Month
  // ======================================
  loadEventsForOrg: (orgId: number, contacts: ContactInfo[]) => Promise<void>;
  clearEvents: () => void;

  // ======================================
  // Actions: Error Handling
  // ======================================
  clearError: () => void;
}

/**
 * EPG Event Store
 *
 * @example
 * ```tsx
 * const { loadEventOptions, eventOptionsByCategory } = useEventStore();
 *
 * // Load event options for a category
 * await loadEventOptions('Events');
 *
 * // Access options
 * const eventOptions = eventOptionsByCategory['Events'];
 * ```
 */
export const useEventStore = create<EventState>()(
  devtools(
    persist(
      (set, get) => ({
        // ======================================
        // Initial State
        // ======================================
        eventsByMonth: null,

        eventOptions: null,
        eventOptionsByCategory: {
          'Events': {
            months: {
              JAN: [], FEB: [], MAR: [], APR: [], MAY: [], JUN: [],
              JUL: [], AUG: [], SEP: [], OCT: [], NOV: [], DEC: []
            },
            isLoading: false,
            lastLoaded: null,
          },
          'Calls & Emails': {
            months: {
              JAN: [], FEB: [], MAR: [], APR: [], MAY: [], JUN: [],
              JUL: [], AUG: [], SEP: [], OCT: [], NOV: [], DEC: []
            },
            isLoading: false,
            lastLoaded: null,
          },
          'Drops': {
            months: {
              JAN: [], FEB: [], MAR: [], APR: [], MAY: [], JUN: [],
              JUL: [], AUG: [], SEP: [], OCT: [], NOV: [], DEC: []
            },
            isLoading: false,
            lastLoaded: null,
          },
        },
        isLoadingEventOptions: false,

        organizationContacts: [],

        fieldDefinitions: null,
        fieldDefinitionsCategories: null,
        fieldDefinitionsLastSync: null,
        isLoadingFieldDefinitions: false,
        isSyncingFieldDefinitions: false,

        isLoadingEvents: false,
        error: null,

        // ======================================
        // Event Options Actions
        // ======================================

        /**
         * Get static fallback event options for a category
         * Used when API call fails or before data is loaded
         */
        getStaticEventOptions: (category: 'Events' | 'Calls & Emails' | 'Drops'): CategoryEventOptions => {
          const staticOptions = getCategoryOptions(category);

          const months: Record<MonthCode, EventOption[]> = {
            JAN: [], FEB: [], MAR: [], APR: [], MAY: [], JUN: [],
            JUL: [], AUG: [], SEP: [], OCT: [], NOV: [], DEC: []
          };

          // Duplicate static options to all months
          Object.keys(months).forEach((month) => {
            months[month as MonthCode] = [...staticOptions];
          });

          logger.warn(`[Event Store] Using static fallback options for ${category} (${staticOptions.length} options)`);

          return {
            months,
            isLoading: false,
            lastLoaded: null, // null indicates fallback
          };
        },

        /**
         * Transform API response to month-based structure
         *
         * IMPORTANT: Backend returns 'name' field but frontend expects 'label'.
         * This function maps 'name' to 'label' for both flat and month-based responses.
         *
         * @param response - API response with flat event options array
         * @param category - Category name for static fallback
         * @returns CategoryEventOptions with months structure
         */
        transformEventOptions: (
          response: EventOptionsResponse | null,
          category: 'Events' | 'Calls & Emails' | 'Drops'
        ): CategoryEventOptions => {
          const months: Record<MonthCode, EventOption[]> = {
            JAN: [], FEB: [], MAR: [], APR: [], MAY: [], JUN: [],
            JUL: [], AUG: [], SEP: [], OCT: [], NOV: [], DEC: []
          };

          // Helper function to map backend event option to frontend format
          // Backend returns 'name', frontend expects 'label'
          interface BackendEventOption {
            id: string;
            name?: string;
            label?: string;
            color?: string | null;
            is_separator?: boolean;
            category_type?: string | null;
          }

          const mapEventOption = (item: BackendEventOption): EventOption => ({
            id: item.id,
            label: item.name || item.label || '',
            color: item.color || null,
            is_separator: item.is_separator || false,
            category_type: item.category_type || null,
          });

          if (!response || !response.months || Object.keys(response.months).length === 0) {
            // Return empty structure if no data
            return {
              months,
              isLoading: false,
              lastLoaded: new Date(),
            };
          }

          // Transform each month's options to map 'name' -> 'label'
          const transformedMonths: Record<MonthCode, EventOption[]> = {
            JAN: [], FEB: [], MAR: [], APR: [], MAY: [], JUN: [],
            JUL: [], AUG: [], SEP: [], OCT: [], NOV: [], DEC: []
          };

          Object.entries(response.months).forEach(([month, options]) => {
            if (month in transformedMonths && Array.isArray(options)) {
              transformedMonths[month as MonthCode] = options.map(mapEventOption);
            }
          });

          return {
            months: transformedMonths,
            isLoading: false,
            lastLoaded: new Date(),
          };
        },

        /**
         * Load event options for a category (Events, Calls & Emails, Drops)
         */
        loadEventOptions: async (category: 'Events' | 'Calls & Emails' | 'Drops') => {
          // Normalize category name: UI uses 'Calls/Emails' but backend expects 'Calls & Emails'
          const normalizedCategory = normalizeCategoryName(category) as 'Events' | 'Calls & Emails' | 'Drops';

          // Mark category as loading
          set((state) => ({
            eventOptionsByCategory: {
              ...state.eventOptionsByCategory,
              [normalizedCategory]: {
                ...state.eventOptionsByCategory[normalizedCategory],
                isLoading: true,
              },
            },
            isLoadingEventOptions: true,
            error: null,
          }));

          try {
            logger.log(`[Event Store] Loading event options for category: ${normalizedCategory}`);

            const result = await epgApi.getEventOptions(normalizedCategory);

            // Transform response to month-based structure
            const transformed = get().transformEventOptions(result, normalizedCategory);

            set((state) => ({
              eventOptions: result, // Keep for backward compatibility
              eventOptionsByCategory: {
                ...state.eventOptionsByCategory,
                [normalizedCategory]: transformed,
              },
              isLoadingEventOptions: false,
            }));

            logger.log(`[Event Store] Loaded event options for ${normalizedCategory}: ${Object.values(transformed.months).reduce((sum, opts) => sum + opts.length, 0)} total options across 12 months`);
          } catch (error) {
            logger.error('[Event Store] Failed to load event options:', error);

            // Use static fallback on API failure
            const fallbackOptions = get().getStaticEventOptions(normalizedCategory);

            set((state) => ({
              eventOptionsByCategory: {
                ...state.eventOptionsByCategory,
                [normalizedCategory]: fallbackOptions,
              },
              error: error instanceof Error ? error.message : 'Failed to load event options',
              isLoadingEventOptions: false,
            }));

            logger.warn(`[Event Store] Loaded static fallback for ${normalizedCategory}: ${Object.values(fallbackOptions.months).reduce((sum, opts) => sum + opts.length, 0)} total options`);
          }
        },

        // ======================================
        // Field Definitions Actions
        // ======================================

        /**
         * Load cached field definitions from backend
         *
         * Fetches field definitions for Pipedrive custom fields.
         * Supports optional category filtering.
         */
        loadFieldDefinitions: async (category?: string) => {
          set({ isLoadingFieldDefinitions: true, error: null });

          try {
            // Normalize category name if provided: UI uses 'Calls/Emails' but backend expects 'Calls & Emails'
            const normalizedCategory = category ? normalizeCategoryName(category) : undefined;
            logger.log(`[Event Store] Loading field definitions (category=${normalizedCategory || 'all'})`);

            const result = await epgApi.getFieldDefinitions(normalizedCategory);

            set({
              fieldDefinitions: result.fields,
              fieldDefinitionsCategories: result.categories,
              fieldDefinitionsLastSync: result.last_sync,
              isLoadingFieldDefinitions: false,
            });

            logger.log(
              `[Event Store] Loaded ${Object.keys(result.fields).length} field definitions` +
              (normalizedCategory ? ` for category: ${normalizedCategory}` : '')
            );
          } catch (error) {
            logger.error('[Event Store] Failed to load field definitions:', error);
            set({
              error: error instanceof Error ? error.message : 'Failed to load field definitions',
              isLoadingFieldDefinitions: false,
            });
          }
        },

        /**
         * Sync field definitions from Pipedrive API
         *
         * Refreshes the cached field definitions by fetching from Pipedrive.
         * Use sparingly as this makes an API call to Pipedrive.
         */
        syncFieldDefinitions: async () => {
          set({ isSyncingFieldDefinitions: true, error: null });

          try {
            logger.log('[Event Store] Syncing field definitions from Pipedrive');

            const result = await epgApi.syncFieldDefinitions();

            // Check for errors
            if (result.errors && result.errors.length > 0) {
              const errorMsg = `Sync completed with errors: ${result.errors.join(', ')}`;
              logger.error(`[Event Store] ${errorMsg}`);
              set({
                error: errorMsg,
              });
            } else {
              logger.log(
                `[Event Store] Synced ${result.fields_updated} field definitions from Pipedrive`
              );
            }

            // Reload field definitions to get the fresh data
            await get().loadFieldDefinitions();

            set({
              isSyncingFieldDefinitions: false,
            });
          } catch (error) {
            logger.error('[Event Store] Failed to sync field definitions:', error);
            set({
              error: error instanceof Error ? error.message : 'Failed to sync field definitions',
              isSyncingFieldDefinitions: false,
            });
          }
        },

        // ======================================
        // Events by Month Actions
        // ======================================

        /**
         * Load events for an organization
         *
         * This is called when an organization is selected.
         * Merges category-specific arrays into single selected_ids array.
         */
        loadEventsForOrg: async (orgId: number, contacts: ContactInfo[]) => {
          set({ isLoadingEvents: true, error: null });

          try {
            logger.log(`[Event Store] Loading events for org ${orgId}`);

            const eventsData = await epgApi.getOrganizationEvents(orgId);

            // Merge category-specific arrays (lines 625-648 in original)
            let mergedEventsByMonth = eventsData.events_by_month;

            // Check if any month has category-specific arrays
            const hasCategories = Object.values(eventsData.events_by_month).some((month) =>
              month.events_selected_ids || month.calls_emails_selected_ids || month.drops_selected_ids
            );

            if (hasCategories) {
              mergedEventsByMonth = { ...eventsData.events_by_month };

              Object.keys(mergedEventsByMonth).forEach((monthCode) => {
                const apiMonth = mergedEventsByMonth[monthCode] || {};
                mergedEventsByMonth[monthCode] = {
                  ...apiMonth,
                  selected_ids: [
                    ...(apiMonth.events_selected_ids || []),
                    ...(apiMonth.calls_emails_selected_ids || []),
                    ...(apiMonth.drops_selected_ids || []),
                  ],
                };
              });
            }

            set({
              eventsByMonth: mergedEventsByMonth,
              organizationContacts: contacts,
              isLoadingEvents: false,
            });

            logger.log(
              `[Event Store] Loaded org ${orgId}: ${contacts.length} contacts, ${Object.keys(mergedEventsByMonth).length} months`
            );
          } catch (error) {
            logger.error(`[Event Store] Failed to load events for org ${orgId}:`, error);
            set({
              error: error instanceof Error ? error.message : 'Failed to load events',
              isLoadingEvents: false,
            });
          }
        },

        /**
         * Clear events (when organization is deselected)
         */
        clearEvents: () => {
          set({
            eventsByMonth: null,
            organizationContacts: [],
          });
        },

        // ======================================
        // Error Handling
        // ======================================

        /**
         * Clear error state
         */
        clearError: () => {
          set({ error: null });
        },
      }),
      {
        name: 'epg-events',
        partialize: (state) => ({
          // Persist only necessary state
          fieldDefinitionsLastSync: state.fieldDefinitionsLastSync,
        }),
      }
    ),
    { name: 'EPG Event Store' }
  )
);
